import {
  DimeCard,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ReceiptText, ShieldAlert } from 'lucide-react';
import { useMemo } from 'react';

import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import { buildListEmptyMessage } from '../../lib/build-list-empty-message.js';
import { makeInvoiceColumns } from '../../modules/invoices/invoice-columns.js';
import type { InvoiceListFilterValue } from '../../modules/invoices/invoice-list-filter-value.js';
import type { InvoiceListSearch } from '../../modules/invoices/invoice-list-search.js';
import { InvoiceStatusFilter } from '../../modules/invoices/invoice-status-filter.js';
import { parseInvoiceListSearch } from '../../modules/invoices/parse-invoice-list-search.js';
import { useInvoicesListQuery } from '../../modules/invoices/use-invoices-list-query.js';
import { useNoStatesAssigned } from '../../modules/invoices/use-no-states-assigned.js';

export const Route = createFileRoute('/invoices/')({
  validateSearch: (search: Record<string, unknown>): InvoiceListSearch =>
    parseInvoiceListSearch(search),
  component: InvoicesIndexPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function InvoicesIndexPage() {
  const { status } = Route.useSearch();
  const navigate = useNavigate({ from: '/invoices/' });
  const statusFilter: InvoiceListFilterValue = status ?? 'ALL';
  const { invoices, loading, error, refetch, businessDay, search, tableProps } =
    useInvoicesListQuery(statusFilter);
  const { noStatesAssigned, statesLoading } = useNoStatesAssigned();
  const columns = useMemo(() => makeInvoiceColumns(businessDay), [businessDay]);
  // A chip is a filter, not a history stop, so it replaces the entry; All leaves the param out.
  const changeStatusFilter = (next: InvoiceListFilterValue) =>
    void navigate({
      search: (previous) => ({ ...previous, status: next === 'ALL' ? undefined : next }),
      replace: true,
    });

  const hasActiveFilter = statusFilter !== 'ALL';
  // State scoping returns no rows for a caller with no state grants, which is not "nothing has shipped".
  const showNoStatesEmptyState = !loading && !error && noStatesAssigned && invoices.length === 0;
  // The generic empty state waits for the states, or a caller with none would see it flash first.
  const showEmptyState =
    !showNoStatesEmptyState &&
    !statesLoading &&
    !loading &&
    !error &&
    invoices.length === 0 &&
    !search &&
    statusFilter === 'ALL';
  const statusFilterControl = (
    <InvoiceStatusFilter value={statusFilter} onChange={changeStatusFilter} />
  );

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Invoices</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Track what has been billed, what has been paid, and what is overdue.
        </DimeCardDescription>
      </DimeCardHeader>

      {error && invoices.length === 0 ? (
        <ListErrorState
          title="Could not load invoices"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : showNoStatesEmptyState ? (
        <ListEmptyState
          icon={<ShieldAlert />}
          title="No states assigned"
          description="Ask an administrator to grant you state access to view invoices."
        />
      ) : showEmptyState ? (
        <>
          {/* "All" leaves out void invoices, so the chips stay reachable even when it is empty. */}
          <div className="mb-4">{statusFilterControl}</div>
          <ListEmptyState
            icon={<ReceiptText />}
            title="No invoices yet"
            description="An invoice is created when a delivery is completed."
          />
        </>
      ) : (
        <DimeDataTable
          columns={columns}
          data={invoices}
          maxBodyHeight="viewport"
          searchColumn="invoiceNumber"
          searchPlaceholder="Search by invoice #…"
          filters={statusFilterControl}
          showViewOptions
          emptyMessage={buildListEmptyMessage('invoices', search, hasActiveFilter)}
          onRowClick={(invoice) => {
            void navigate({ to: '/invoices/$invoiceId', params: { invoiceId: invoice.id } });
          }}
          loadingRowCount={tableProps.pageSize}
          exportFileName="invoices"
          {...tableProps}
        />
      )}
    </DimeCard>
  );
}
